/**
 * A take's result, as recorded: its scores, counts, and a score for each position of
 * the material (each measure, by default). Positions are scored through the engine's own
 * combining, so a position score has the same single credit source as everything else.
 */

import { CombiningConfig, ScorePoint, ViewName, scoreViews } from '../combining';
import { JudgedTake } from '../judgedTake';
import { ScoringConfig, scoreTake } from '../preset';
import { RealizedScore } from '../types';

/** The score group label a position is read from: a position is a measure. */
export const POSITION_GROUP = 'measure';

/** One measure of a score: its label and its first notated beat. */
export interface MaterialPosition {
  readonly position: string;
  readonly firstBeat: number;
}

export interface TakeCounts {
  /** Notes of the hands played. */
  readonly graded: number;
  readonly matched: number;
  readonly missed: number;
  readonly unreached: number;
  /** Matched with the wrong pitch. */
  readonly wrongPitch: number;
  readonly extras: number;
}

export interface PositionScore {
  /** The group label, e.g. the measure number. */
  readonly position: string;
  /** The earliest notated onset among the position's notes, for ordering. */
  readonly beat: number;
  /** Mean credit of the reached notes, per view; null when a view has none there. */
  readonly combined: number | null;
  readonly left: number | null;
  readonly right: number | null;
  /** Reached notes counted in the combined view. */
  readonly notes: number;
}

export interface TakeResult {
  /** The scoring's latest value per view: the take's grade. */
  readonly views: Readonly<Record<ViewName, number | null>>;
  readonly counts: TakeCounts;
  /** In score order. */
  readonly positions: readonly PositionScore[];
}

function positionCombining(groupKey: string): CombiningConfig {
  return {
    pile: { unit: 'note' },
    levels: [
      {
        scope: { kind: 'bounded', boundary: { by: 'group', key: groupKey }, unreached: 'excluded' },
        input: { kind: 'credit' },
        statistic: 'mean',
      },
    ],
  };
}

function valueByGroup(points: readonly ScorePoint[]): ReadonlyMap<string, ScorePoint> {
  return new Map(
    points
      .filter((scored) => scored.group !== null)
      .map((scored): [string, ScorePoint] => [scored.group as string, scored]),
  );
}

/** A score's measures, in score order: every position a take on it can be scored at. */
export function materialPositions(score: RealizedScore): MaterialPosition[] {
  const firstBeat = score.notes.reduce<Record<string, number>>((beats, written) => {
    const label = written.groups?.[POSITION_GROUP];
    return label === undefined || (beats[label] !== undefined && beats[label] <= written.onsetBeat)
      ? beats
      : { ...beats, [label]: written.onsetBeat };
  }, {});
  return Object.keys(firstBeat)
    .map((position) => ({ position, firstBeat: firstBeat[position] }))
    .sort((a, b) => a.firstBeat - b.firstBeat || a.position.localeCompare(b.position));
}

export function positionScores(
  take: JudgedTake,
  groupKey: string = POSITION_GROUP,
): PositionScore[] {
  const views = scoreViews(take, positionCombining(groupKey));
  const left = valueByGroup(views.left.points);
  const right = valueByGroup(views.right.points);
  const firstBeat = take.notes.reduce<Record<string, number>>((beats, judgment) => {
    const label = judgment.groups[groupKey];
    return label === undefined || (beats[label] !== undefined && beats[label] <= judgment.onsetBeat)
      ? beats
      : { ...beats, [label]: judgment.onsetBeat };
  }, {});
  return views.combined.points
    .filter((scored) => scored.group !== null)
    .map((scored) => {
      const position = scored.group as string;
      return {
        position,
        beat: firstBeat[position],
        combined: scored.value,
        left: left.get(position)?.value ?? null,
        right: right.get(position)?.value ?? null,
        notes: scored.count,
      };
    })
    .sort((a, b) => a.beat - b.beat);
}

export function takeCounts(take: JudgedTake): TakeCounts {
  const graded = take.notes.filter((judgment) => judgment.status !== 'not-graded');
  const count = (status: string) => graded.filter((judgment) => judgment.status === status).length;
  return {
    graded: graded.length,
    matched: count('matched'),
    missed: count('missed'),
    unreached: count('unreached'),
    wrongPitch: graded.filter((judgment) => judgment.pitchCorrect === false).length,
    extras: take.extras.length,
  };
}

export function takeResult(take: JudgedTake, scoring: ScoringConfig): TakeResult {
  const scored = scoreTake(take, scoring);
  return {
    views: {
      combined: scored.views.combined.latest?.value ?? null,
      left: scored.views.left.latest?.value ?? null,
      right: scored.views.right.latest?.value ?? null,
    },
    counts: takeCounts(take),
    positions: positionScores(take),
  };
}
